import { action, observable, computed, isObservableArray } from "mobx";
import { AnyObj, FieldProps, FormLayoutProps, FormLayoutType, SearchAdvancedStoreType, FormDatas } from "../../../lib";
import { isEmpty, needSync } from "../../../utils";
import { isCustomField, isReadOnlySetting, isSortSetting, removeSuffix } from "../util/browser-config";
import { SearchAdvancedInitAllDatas } from "./Store";

class BrowserConfigStore {
  parent: SearchAdvancedStoreType;

  constructor(parent: SearchAdvancedStoreType) {
    this.parent = parent;
  }
  // allDatas, 用于重置
  allDatas: SearchAdvancedInitAllDatas = {
    module: "",
    conditionId: "",
    layout: [],
    data: {},
    groups: [],
    items: {},
  };
  // data
  datas: AnyObj = {}; // 所有字段值
  @observable.shallow statusDatas: AnyObj = {}; // datas数据内 标记状态的数据值，xxx_visible xxx_readonly xxx_sort
  // 禁用
  disableFieldsKeys: Set<string> = new Set([]); // 禁用字段key值集合，常用分组、其他分组、常用条件设置均禁用
  disableItemsKeys: Set<string> = new Set([]); // 禁用字段的items配置的key集合
  // 用于重置布局相关的数据
  allFields: FieldProps[] = [];
  commonCondition: FieldProps[] = []; // 常用分组条件
  commonLayoutConfig: any[] = []; // 常用分组个人设置
  // items 和 layout 的映射关系，用于禁用条件提示(选中常用筛选)
  itemColMapping: Map<string, AnyObj> = new Map();
  // 隐藏
  customHideLayouts: Set<string | undefined> = new Set([]); // 标记其他分组 隐藏的字段
  hideLayouts: Set<string> = new Set([]); // 标记常用分组隐藏的字段
  // 只读
  @observable.shallow wholeRowReadOnlyFieldsKeys: Map<string, string[]> = new Map(); // 整行只读标记
  needTransBrowserConfigLayout?: boolean = false; // 标记是否需要转换layout数据(以前台个人设置为准的不需要)
  @computed get readOnlyItems() {
    let readOnlyItems: Set<string> = new Set([]);
    Object.keys(this.statusDatas).forEach((dataKey) => {
      if (isReadOnlySetting(dataKey) && this.statusDatas[dataKey]) {
        const key = dataKey.slice(0, dataKey.indexOf('_readonly'));
        readOnlyItems.add(key);
        if (this.wholeRowReadOnlyFieldsKeys?.has(key)) {
          this.wholeRowReadOnlyFieldsKeys.get(key)?.forEach(k => readOnlyItems.add(k));
        }
      }
    })
    return readOnlyItems;
  }
  @computed get maxOrder() {
    let order = -1;
    Object.keys(this.statusDatas)?.forEach((daKey) => {
      if (isSortSetting(daKey)) {
        if (this.statusDatas[daKey] > order) order = this.statusDatas[daKey];
       }
    })
    return order;
  }
  // 有后台设置
  @computed get hasDefaultConfig() {
    return Object.keys(this.statusDatas).length > 0;
  }
  @action
  hasDefaultData = (datakey: string) => {
    return needSync(datakey, this.datas);
  }
  @action
  setState = (params: Partial<BrowserConfigStoreType>) => {
    Object.keys(params).forEach((key) => {
      const storeKey = key as keyof BrowserConfigStoreType;
      // @ts-ignore
      this[storeKey] = params[storeKey];
    })
  }
  @action
  reset = () => {
    return this.parent.init(this.allDatas, true);
  }

  /* 后台设置禁用/隐藏的常用分组字段，非空的字段才需要展示在其他分组内 */
  @action
  isFieldEmpty = (itemKey: string, layout: FormLayoutType[], _otherConfigs?: AnyObj) => {
    const { mustDisplayInOtherGroup, ...otherParams } = _otherConfigs || {};
    if (!(mustDisplayInOtherGroup && this.hasDefaultConfig)) return false; // 仅处理常用分组条件被隐藏/禁用，且存在后台设置的情况
    const { nowlayout } = this.parent.transDatasStore.getLayoutFromItemKey(itemKey, layout, otherParams) as unknown as FormLayoutProps;
    if (nowlayout) {
      let { items: allItems, cascadeRules } = nowlayout;
      if (cascadeRules) {
        Object.keys(cascadeRules).forEach((dakey) => {
          cascadeRules[dakey] && Object.keys(cascadeRules[dakey]).forEach((items: any) => {
            if (Array.isArray(items) || isObservableArray(items)) {
              allItems = allItems.concat(items);
            } else if (items?.items) {
              allItems = allItems.concat(items?.items);
            }
          })
        })
      }
      const flag = allItems.find((k: string) => {
        return k !== itemKey && !isEmpty(_otherConfigs?.datas?.[k])
      })
      if (!flag) return true;
    }
    return false;
  }

  @action
  judgeConditionNum = () => {
    let datas = this.parent.formStore.datas;
    if (this.parent.filterUselessDatas) { // 过滤无用data
      datas = this.parent.transDatasStore.filterUselessDatas({
        data: datas,
        items: this.parent.formStore.items,
        layout: this.parent.formStore.layout,
        groups: this.parent.formStore.groups,
        customLayout: this.parent.formStore.customLayout,
      });
    }
    return this.parent.judgeEmptyFunc(this.filterDatas(datas));
  }

  @action
  filterDatas = (datas: FormDatas) => {
    if (isEmpty(this.datas) || Object.keys(this.statusDatas).length <= 0) return datas;
    /*
      过滤掉当前隐藏/禁用的默认条件，不作为常用筛选条件
      1.禁用读disableItemsKeys
      2.隐藏：以当前字段是否被隐藏为准。其他分组的条件以customHideLayouts为准，常用分组以layout当前状态为准(常用分组可在常用条件设置修改)
    */
    let result: FormDatas = {};
    Object.keys(datas)?.forEach((daKey) => {
      const iscustom = isCustomField(daKey);
      const key = iscustom ? removeSuffix(daKey) : daKey;
      // 禁用字段
      if (this.disableItemsKeys.has(key)) return;
      // 其他分组数据隐藏：this.statusDatas对应的数据visible=false(其他分组的隐藏状态不可更改)
      if (iscustom && this.statusDatas[`${daKey}_visible`] === false) return;
      if (!iscustom) {
        // 常用分组数据隐藏：layout被隐藏(hide=true|disabled=true)
        const { nowlayout } = this.parent.transDatasStore.getLayoutFromItemKey(key, this.parent.formStore.layout, { datas, value: datas[daKey] }) as unknown as FormLayoutProps;
        if (nowlayout && (nowlayout.hide || nowlayout.disabled)) return;
      }
      result = { ...result, [daKey]: datas[daKey] };
    })
    return result;
  }
}

export type BrowserConfigStoreType = Pick<BrowserConfigStore, keyof BrowserConfigStore>;
export default BrowserConfigStore;